import { useState } from "react";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { DateField } from "@/components/common/masked-form-field";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { portalLawsuits, type PortalCollectionApi } from "@/features/portal/api";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import { isoToMaskedDate, maskedDateToISO } from "@/lib/masks";
import {
  CollectionCard,
  type Column,
  type PortalRecord,
  usePortalCollection,
} from "./collection-card";
import { PortalCrudDialog } from "./crud-dialog";
import { MoneyInput } from "./fields";
import { FIELD_LABEL } from "./fields";
import { dash, money, num, str } from "./record";
import type { WorksheetHeader } from "./portal-financials";

const nonNegMoney = Yup.string().test(
  "non-negative",
  "Must be zero or more",
  (v) => !v || dollarsToCents(v) >= 0,
);

/** A header field currently reflected in local state, persisted on change/blur. */
function useHeaderField(
  header: WorksheetHeader,
  save: (patch: Record<string, unknown>) => void,
) {
  const [values, setValues] = useState<Record<string, unknown>>(header);
  function set(field: string, value: unknown) {
    setValues((v) => ({ ...v, [field]: value }));
  }
  function commit(field: string, value: unknown) {
    set(field, value);
    save({ [field]: value });
  }
  return { values, set, commit };
}

interface Props {
  header: WorksheetHeader;
  save: (patch: Record<string, unknown>) => void;
}

/** A boolean toggle that persists to the worksheet header on change. */
function ToggleRow({
  label,
  checked,
  onChange,
  children,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="grid gap-3">
      <label className="flex items-center gap-2 text-sm font-medium">
        <Checkbox checked={checked} onCheckedChange={(c) => onChange(c === true)} />
        <span>{label}</span>
      </label>
      {checked && children && <div className="grid gap-3 pl-6">{children}</div>}
    </div>
  );
}

/** A plain (non-Formik) text/date/money header field that autosaves on blur. */
function HeaderText({
  label,
  value,
  onCommit,
  type = "text",
}: {
  label: string;
  value: string;
  onCommit: (v: string) => void;
  type?: "text" | "money";
}) {
  const [local, setLocal] = useState(value);
  return (
    <div className="grid gap-2">
      <Label className={FIELD_LABEL}>{label}</Label>
      <Input
        value={local}
        inputMode={type === "money" ? "decimal" : undefined}
        onChange={(e) => {
          const raw = e.target.value;
          if (type === "money" && raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;
          setLocal(raw);
        }}
        onBlur={() => onCommit(local)}
      />
    </div>
  );
}

export function FinancialTab({ header, save }: Props) {
  const { values, commit } = useHeaderField(header, save);
  const isYes = (k: string) => values[k] === "Y" || values[k] === true;
  const setYesNo = (k: string, v: boolean) => commit(k, v ? "Y" : "N");
  const bankruptNow = isYes("bankrupt_now_sw");

  return (
    <div className="grid gap-6">
      <Card className="grid gap-5 p-4">
        <h3 className="font-semibold">Bankruptcy</h3>
        <ToggleRow
          label="Currently in bankruptcy"
          checked={bankruptNow}
          onChange={(v) => setYesNo("bankrupt_now_sw", v)}
        >
          <div className="grid grid-cols-2 gap-4">
            <HeaderText
              label="Petition no."
              value={str(values as PortalRecord, "bankruptcy_petition")}
              onCommit={(v) => commit("bankruptcy_petition", v)}
            />
            <HeaderText
              label="Location filed"
              value={str(values as PortalRecord, "bankruptcy_location")}
              onCommit={(v) => commit("bankruptcy_location", v)}
            />
          </div>
        </ToggleRow>
        <ToggleRow
          label="Filed bankruptcy in the past"
          checked={isYes("bankrupt_sw")}
          onChange={(v) => setYesNo("bankrupt_sw", v)}
        >
          <div className="grid grid-cols-2 gap-4">
            <HeaderDate
              label="Date filed"
              value={str(values as PortalRecord, "bankruptcy_filed")}
              onCommit={(v) => commit("bankruptcy_filed", v)}
            />
            <HeaderDate
              label="Date dismissed"
              value={str(values as PortalRecord, "bankruptcy_dismissed")}
              onCommit={(v) => commit("bankruptcy_dismissed", v)}
            />
          </div>
        </ToggleRow>
      </Card>

      <Card className="grid gap-5 p-4">
        <h3 className="font-semibold">Trusts, funds & assets held</h3>
        <ToggleRow
          label="Beneficiary of a trust or estate"
          checked={isYes("trustee_sw")}
          onChange={(v) => setYesNo("trustee_sw", v)}
        >
          <div className="grid grid-cols-2 gap-4">
            <HeaderText
              label="Trust name"
              value={str(values as PortalRecord, "trust_name")}
              onCommit={(v) => commit("trust_name", v)}
            />
            <HeaderText
              label="Trustee"
              value={str(values as PortalRecord, "trust_trustee")}
              onCommit={(v) => commit("trust_trustee", v)}
            />
            <HeaderMoney
              label="Amount"
              cents={num(values as PortalRecord, "trust_amount")}
              onCommit={(cents) => commit("trust_amount", cents)}
            />
            <HeaderDate
              label="Date"
              value={str(values as PortalRecord, "trust_date")}
              onCommit={(v) => commit("trust_date", v)}
            />
          </div>
        </ToggleRow>

        <ToggleRow
          label="Funds held by a third party"
          checked={isYes("funds_held_sw")}
          onChange={(v) => setYesNo("funds_held_sw", v)}
        >
          <div className="grid grid-cols-2 gap-4">
            <HeaderMoney
              label="Amount"
              cents={num(values as PortalRecord, "funds_held_amount")}
              onCommit={(cents) => commit("funds_held_amount", cents)}
            />
            <HeaderText
              label="Location"
              value={str(values as PortalRecord, "funds_held_location")}
              onCommit={(v) => commit("funds_held_location", v)}
            />
          </div>
        </ToggleRow>

        <ToggleRow
          label="Has a safe deposit box"
          checked={isYes("safe_dep_sw")}
          onChange={(v) => setYesNo("safe_dep_sw", v)}
        >
          <div className="grid grid-cols-2 gap-4">
            <HeaderText
              label="Bank name"
              value={str(values as PortalRecord, "safe_deposit_name")}
              onCommit={(v) => commit("safe_deposit_name", v)}
            />
            <HeaderText
              label="Box no."
              value={str(values as PortalRecord, "safe_deposit_box")}
              onCommit={(v) => commit("safe_deposit_box", v)}
            />
            <HeaderText
              label="Address"
              value={str(values as PortalRecord, "safe_deposit_address")}
              onCommit={(v) => commit("safe_deposit_address", v)}
            />
            <HeaderText
              label="Contents"
              value={str(values as PortalRecord, "safe_deposit_content")}
              onCommit={(v) => commit("safe_deposit_content", v)}
            />
            <HeaderMoney
              label="Estimated value"
              cents={num(values as PortalRecord, "safe_deposit_value")}
              onCommit={(cents) => commit("safe_deposit_value", cents)}
            />
          </div>
        </ToggleRow>
      </Card>

      <Card className="grid gap-5 p-4">
        <h3 className="font-semibold">Foreign / abroad</h3>
        <ToggleRow
          label="Lived or traveled abroad in the last 6 years"
          checked={isYes("abroad_sw")}
          onChange={(v) => setYesNo("abroad_sw", v)}
        >
          <div className="grid grid-cols-2 gap-4">
            <HeaderDate
              label="From"
              value={str(values as PortalRecord, "abroad_from")}
              onCommit={(v) => commit("abroad_from", v)}
            />
            <HeaderDate
              label="To"
              value={str(values as PortalRecord, "abroad_to")}
              onCommit={(v) => commit("abroad_to", v)}
            />
          </div>
        </ToggleRow>
        <ToggleRow
          label="Owns assets outside the United States"
          checked={isYes("nonus_sw")}
          onChange={(v) => setYesNo("nonus_sw", v)}
        >
          <HeaderText
            label="Description"
            value={str(values as PortalRecord, "nonus_asset_desc")}
            onCommit={(v) => commit("nonus_asset_desc", v)}
          />
        </ToggleRow>
      </Card>

      <LawsuitsSection />
    </div>
  );
}

/** Header date field: masked MM/DD/YYYY UI, autosaves the ISO string. */
function HeaderDate({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: string;
  onCommit: (iso: string | null) => void;
}) {
  const [local, setLocal] = useState(isoToMaskedDate(value));
  return (
    <div className="grid gap-2">
      <Label className={FIELD_LABEL}>{label}</Label>
      <Input
        value={local}
        inputMode="numeric"
        placeholder="MM/DD/YYYY"
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => onCommit(local ? maskedDateToISO(local) || null : null)}
      />
    </div>
  );
}

/** Header money field: dollar-string UI, autosaves integer cents. */
function HeaderMoney({
  label,
  cents,
  onCommit,
}: {
  label: string;
  cents: number | null;
  onCommit: (cents: number) => void;
}) {
  const [local, setLocal] = useState(cents == null ? "" : centsToDollars(cents));
  return (
    <div className="grid gap-2">
      <Label className={FIELD_LABEL}>{label}</Label>
      <div className="relative">
        <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm">
          $
        </span>
        <Input
          className="pl-7"
          value={local}
          inputMode="decimal"
          placeholder="0.00"
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) setLocal(raw);
          }}
          onBlur={() => onCommit(dollarsToCents(local))}
        />
      </div>
    </div>
  );
}

// --- Lawsuits ----------------------------------------------------------------

interface LawsuitValues {
  description: string;
  amount: string;
  date_filed: string;
}

function LawsuitDialog({
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const initial: LawsuitValues = {
    description: str(record ?? {}, "description"),
    amount: centsToDollars(num(record ?? {}, "amount") ?? 0),
    date_filed: isoToMaskedDate(str(record ?? {}, "date_filed")),
  };

  async function onSave(v: LawsuitValues) {
    const payload = {
      description: v.description,
      amount: dollarsToCents(v.amount),
      date_filed: v.date_filed ? maskedDateToISO(v.date_filed) || null : null,
    };
    if (editing && record) await portalLawsuits.update(record.id, payload);
    else await portalLawsuits.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Lawsuit"
      initialValues={initial}
      validationSchema={Yup.object({
        description: Yup.string().trim().required("Required"),
        amount: nonNegMoney,
      })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="description" label="Description" />
      <div className="grid grid-cols-2 gap-4">
        <MoneyInput name="amount" label="Amount" />
        <DateField name="date_filed" label="Date filed" />
      </div>
    </PortalCrudDialog>
  );
}

function LawsuitsSection() {
  const { rows, loading, reload } = usePortalCollection(
    portalLawsuits as PortalCollectionApi<PortalRecord>,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });

  const columns: Column<PortalRecord>[] = [
    { header: "Description", cell: (r) => dash(str(r, "description")) },
    { header: "Amount", cell: (r) => money(r, "amount") },
    { header: "Date filed", cell: (r) => dash(str(r, "date_filed")) },
  ];

  return (
    <>
      <CollectionCard
        title="Lawsuits"
        addLabel="Add Lawsuit"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => portalLawsuits.remove(row.id)}
        onDeleted={reload}
      />
      <LawsuitDialog
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}
